import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { categories, categoryMap } from '@/mocks/categories';
import { articleMap, articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const category = categoryMap[article.category];
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: category ? category.name : '资源分类', to: category ? `/category/${article.category}` : '/category/free-website-builders' },
    { label: article.title },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.to ? `https://www.free-site-nav.com${item.to}` : undefined,
    })),
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '免费资源导航',
    },
    publisher: {
      '@type': 'Organization',
      name: '免费资源导航',
    },
    url: `https://www.free-site-nav.com/article/${slug}`,
    image: article.image,
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 免费资源导航`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.free-site-nav.com/article/${slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        ogUrl={`https://www.free-site-nav.com/article/${slug}`}
        structuredData={{ ...breadcrumbSchema, ...articleSchema }}
      />

      <Navbar />
      <ScrollToTop />

      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        {/* Article Header */}
        <article className="w-full bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-3xl mx-auto py-8 md:py-12">
            <header>
              {category && (
                <Link
                  to={`/category/${article.category}`}
                  className="inline-flex items-center px-3 py-1 bg-primary-100 text-primary-700 text-xs rounded-full mb-4 cursor-pointer whitespace-nowrap hover:bg-primary-200 transition-colors"
                >
                  {category.name}
                </Link>
              )}
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-400 mb-6">
                <span>{article.date}</span>
                <span>{article.readTime}</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-8">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </header>

            {/* Featured Image */}
            <div className="w-full rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-auto object-cover"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Share Section */}
            <div className="border-t border-background-200 mt-10 pt-6">
              <p className="text-sm text-foreground-500 mb-3">分享这篇文章：</p>
              <div className="flex items-center gap-3">
                <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap" aria-label="分享到微信">
                  <i className="ri-wechat-line text-foreground-600"></i>
                </button>
                <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap" aria-label="分享到微博">
                  <i className="ri-weibo-line text-foreground-600"></i>
                </button>
                <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap" aria-label="复制链接">
                  <i className="ri-link text-foreground-600"></i>
                </button>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full bg-background-100 py-10 md:py-14">
            <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-8">相关文章</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6" data-product-shop>
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden cursor-pointer transition-all duration-300"
                  >
                    <div className="relative w-full h-40 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        title={ra.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 font-heading leading-snug line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-2">{ra.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}